import { PRODUCT_QUERY_KEYS } from './keys';

import type {
  ProductsAllRequest,
  ProductsAllResponse,
  ProductsCollectionRequest,
  ProductsCollectionResponse,
  ProductsByIdsRequest,
  ProductsByIdsResponse,
  TopRankedProductsRequest,
  TopRankedProductsResponse,
  ProductsCollectionCompetitorRequest,
  ProductsCollectionCompetitorResponse,
  ProductTaxonomy,
  ProductBrandTaxonomy,
  CategoryTaxonomy,
  ProductBrowseRequest,
  ProductBrowseResponse,
  ProductsDetailRequest,
  ProductsDetailResponse,
  ProductMetaRequest,
  ProductMetaResponse,
  ProductSearchRequest,
  ProductSearchResponse,
  ListRecommendationsRequest,
  ListRecommendationsResponse,
  AIInsightRequest,
  AIInsightResponse,
  QueryListRecommendationsRequest,
  ProductsRecommendRequest,
  ProductsRecommendResponse,
  RoutineRecommendationsRequest,
  RoutineRecommendationsResponse,
} from './types';
import { productAPIClient } from '../productAPIClient';
import { queryOptions } from '@tanstack/react-query';
import { mutationOptions } from '../../common/utils';

export const PRODUCT_QUERIES = {
  getProductsAll: (params?: ProductsAllRequest) =>
    queryOptions<ProductsAllResponse>({
      queryKey: PRODUCT_QUERY_KEYS.all(params),
      queryFn: () => productAPIClient.getProductsAll(params),
    }),
  getProductsCollections: (params?: ProductsCollectionRequest) =>
    queryOptions<ProductsCollectionResponse>({
      queryKey: PRODUCT_QUERY_KEYS.collections(params),
      queryFn: () => productAPIClient.getProductsCollections(params),
    }),
  getProductsByIds: (params: ProductsByIdsRequest) =>
    queryOptions<ProductsByIdsResponse>({
      queryKey: PRODUCT_QUERY_KEYS.byIds(params),
      queryFn: () => productAPIClient.getProductsByIds(params),
      enabled: params.ids.length > 0,
    }),
  getProductsDetail: (params: ProductsDetailRequest) =>
    queryOptions<ProductsDetailResponse>({
      queryKey: PRODUCT_QUERY_KEYS.detail(params),
      queryFn: () => productAPIClient.getProductsDetail(params),
      enabled: Boolean(params.slug),
    }),
  getProductMeta: (params: ProductMetaRequest) =>
    queryOptions<ProductMetaResponse>({
      queryKey: PRODUCT_QUERY_KEYS.byIdMeta(params),
      queryFn: () => productAPIClient.getProductMeta(params),
      enabled: Boolean(params.productId),
    }),
  getTopRankedProducts: (params?: TopRankedProductsRequest) =>
    queryOptions<TopRankedProductsResponse>({
      queryKey: PRODUCT_QUERY_KEYS.topRanked(params),
      queryFn: () => productAPIClient.getTopRankedProducts(params),
    }),
  getProductTaxonomy: () =>
    queryOptions<ProductTaxonomy>({
      queryKey: PRODUCT_QUERY_KEYS.taxonomy(),
      queryFn: () => productAPIClient.getProductTaxonomy(),
    }),
  getProductBrandTaxonomy: () =>
    queryOptions<ProductBrandTaxonomy>({
      queryKey: PRODUCT_QUERY_KEYS.brandTaxonomy(),
      queryFn: () => productAPIClient.getProductBrandTaxonomy(),
    }),
  getProductsCollectionCompetitor: (params: ProductsCollectionCompetitorRequest) =>
    queryOptions<ProductsCollectionCompetitorResponse>({
      queryKey: PRODUCT_QUERY_KEYS.competitorCollections(params),
      queryFn: () => productAPIClient.getProductsCollectionCompetitor(params),
    }),
  getCategoryTaxonomy: () =>
    queryOptions<CategoryTaxonomy>({
      queryKey: PRODUCT_QUERY_KEYS.categoryTaxonomy(),
      queryFn: () => productAPIClient.getCategoryTaxonomy(),
    }),
  getAllCategoryTaxonomy: () =>
    queryOptions<CategoryTaxonomy>({
      queryKey: PRODUCT_QUERY_KEYS.allCategoryTaxonomy(),
      queryFn: () => productAPIClient.getAllCategoryTaxonomy(),
    }),
  getProductBrowse: (params?: ProductBrowseRequest) =>
    queryOptions<ProductBrowseResponse>({
      queryKey: PRODUCT_QUERY_KEYS.browse(params),
      queryFn: () => productAPIClient.getProductBrowse(params),
    }),
  getProductSearch: (params: ProductSearchRequest) =>
    queryOptions<ProductSearchResponse>({
      queryKey: PRODUCT_QUERY_KEYS.search(params),
      queryFn: () => productAPIClient.getProductSearch(params),
    }),
  getListRecommendations: (params: QueryListRecommendationsRequest) =>
    queryOptions<ListRecommendationsResponse>({
      queryKey: PRODUCT_QUERY_KEYS.recommendations(params),
      queryFn: () => productAPIClient.getListRecommendations(params as ListRecommendationsRequest),
      enabled: Boolean(params?.productId),
    }),
  getAIInsight: (params: AIInsightRequest) =>
    queryOptions<AIInsightResponse>({
      queryKey: PRODUCT_QUERY_KEYS.aiInsight(params),
      queryFn: () => productAPIClient.getAIInsight(params),
      enabled: Boolean(params.orgProductId && params.altProductId),
    }),
  getRecommendedProducts: (params: ProductsRecommendRequest) =>
    queryOptions<ProductsRecommendResponse>({
      queryKey: PRODUCT_QUERY_KEYS.recommend(params),
      queryFn: () => productAPIClient.getRecommendedProducts(params),
    }),
  getRoutineRecommendations: (params: RoutineRecommendationsRequest) =>
    queryOptions<RoutineRecommendationsResponse>({
      queryKey: PRODUCT_QUERY_KEYS.routineRecommendations(params),
      queryFn: () => productAPIClient.getRoutineRecommendations(params),
      enabled: Boolean(params.productId),
    }),
};

export const PRODUCT_MUTATIONS = {
  getProductsByIds: () => {
    return mutationOptions({
      mutationFn: productAPIClient.getProductsByIds,
    });
  },
  getAIInsight: () => {
    return mutationOptions({
      mutationFn: productAPIClient.getAIInsight,
    });
  },
};
